import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Scale,
  ShieldCheck,
  FileText,
  AlertTriangle,
  Download,
  ExternalLink,
} from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";

// Commission / brokerage / trail — disclosed quarterly (refreshed against
// AMFI / AMC publication cycle).  Grouped by SEBI scheme classification.
const SCHEME_GROUPS = [
  {
    group: "Equity Schemes",
    items: [
      { name: "Equity Funds",          trail: "0.04 % – 1.29 %" },
      { name: "ELSS Funds",            trail: "0.21 % – 1.40 %" },
    ],
  },
  {
    group: "Hybrid Schemes",
    items: [
      { name: "Asset Allocation Funds", trail: "0.50 % – 1.40 %" },
      { name: "Arbitrage Funds",        trail: "0.09 % – 0.68 %" },
      { name: "Hybrid Funds",           trail: "0.09 % – 1.40 %" },
    ],
  },
  {
    group: "Debt Schemes",
    items: [
      { name: "Gilt Funds",             trail: "0.08 % – 0.95 %" },
      { name: "Income & Bond Funds",    trail: "0.01 % – 1.10 %" },
      { name: "Short Term Funds",       trail: "0.13 % – 0.72 %" },
      { name: "Liquid & Floating Rate", trail: "0.03 % – 0.10 %" },
    ],
  },
  {
    group: "Other Schemes",
    items: [
      { name: "Index Funds",            trail: "0.06 % – 0.72 %" },
    ],
  },
];

const DISCLOSURE_PERIOD = "April – June 2026";

export default function Commissions() {
  return (
    <PublicLayout title="Commissions on Mutual Funds">
      <Hero />
      <Disclosure />
      <RatesTable />
      <SchemeDownload />
      <DistributorNote />
      <BackToInvestors />
    </PublicLayout>
  );
}

/* ─────────────────── HERO ─────────────────── */
function Hero() {
  return (
    <section
      data-testid="commissions-hero"
      className="relative overflow-hidden border-b border-white/5"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full bg-[#C8A96B]/[0.06] blur-[160px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-6 md:pt-10 pb-14 md:pb-20">
        <Link
          to="/private-investors"
          data-testid="commissions-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55 hover:text-[#C8A96B] transition mb-10"
        >
          <ArrowLeft size={12} />
          Private Investors
        </Link>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] tracking-[0.2em] uppercase text-white/70 mb-8">
          <Scale size={12} className="text-[#C8A96B]" />
          SEBI / RBI disclosure
        </div>
        <h1 className="font-light text-[36px] leading-[1.08] sm:text-[52px] lg:text-[64px] tracking-[-0.025em] max-w-4xl">
          Commissions on{" "}
          <span className="font-normal italic text-[#C8A96B]">mutual funds.</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-white/65 max-w-3xl leading-[1.55] font-light">
          Full transparency on every rupee of commission Kinntegra Wealth
          Private Limited (AMFI Registered Mutual Funds Distributor — ARN-145633)
          earns from each fund-house we distribute.
        </p>
      </div>
    </section>
  );
}

/* ─────────────────── REGULATORY DISCLOSURE BAND ─────────────────── */
function Disclosure() {
  return (
    <section
      data-testid="commissions-disclosure"
      className="py-14 md:py-16 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-4">
          <SectionEyebrow>Regulatory citation</SectionEyebrow>
          <h2 className="mt-4 text-2xl md:text-3xl font-light tracking-[-0.02em] leading-[1.2]">
            As per{" "}
            <span className="italic text-[#C8A96B]">SEBI &amp; RBI</span>{" "}
            guidelines.
          </h2>
        </div>
        <div className="md:col-span-8 space-y-5">
          <CornerCard>
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} className="text-[#C8A96B] mt-1 shrink-0" />
              <p className="text-sm md:text-base text-white/70 leading-relaxed">
                Kinntegra is offered commissions for investments made under
                broker code{" "}
                <span className="font-mono text-white/90">ARN-145633</span>.
                Any commission receivable is{" "}
                <span className="text-white">subject to claw-back</span> as per
                the guidelines set by the regulator and interpreted &amp; laid
                down by AMCs.
              </p>
            </div>
          </CornerCard>
          <CornerCard>
            <div className="flex items-start gap-3">
              <Scale size={18} className="text-[#C8A96B] mt-1 shrink-0" />
              <p className="text-sm md:text-base text-white/70 leading-relaxed">
                In accordance with SEBI circular{" "}
                <span className="font-mono text-white/90">
                  SEBI/IMD/CIR No. 4/168230/09
                </span>{" "}
                and RBI guidelines on Marketing / Distribution of Mutual Funds,
                the following are the details of commissions earned by
                Kinntegra Wealth Private Limited.
              </p>
            </div>
          </CornerCard>
          <CornerCard>
            <div className="flex items-start gap-3">
              <FileText size={18} className="text-[#C8A96B] mt-1 shrink-0" />
              <p className="text-sm md:text-base text-white/70 leading-relaxed">
                The initial registration date for{" "}
                <span className="font-mono text-white/90">ARN-145633</span>{" "}
                is <span className="text-white">19th May, 2018</span> and the
                current validity of the ARN is{" "}
                <span className="text-white">18th May, 2027</span>.
              </p>
            </div>
          </CornerCard>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── RATES TABLE ─────────────────── */
function RatesTable() {
  // Flatten with group-header rows for the desktop table.
  let rowIdx = 0;
  return (
    <section
      data-testid="commissions-rates"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>Commission, brokerage &amp; trail</SectionEyebrow>
        <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.15] max-w-3xl">
          Trail commissions{" "}
          <span className="italic text-[#C8A96B]">by scheme category.</span>
        </h2>
        <p className="mt-6 text-base text-white/55 font-light leading-relaxed max-w-2xl">
          Disclosure period:{" "}
          <span className="text-white">{DISCLOSURE_PERIOD}</span>. Ranges
          below are the prevailing trail (annualised, per-AMC) across the
          schemes we distribute. Refreshed quarterly when AMCs publish
          revised rates.
        </p>

        {/* Desktop table */}
        <div className="mt-12 hidden md:block">
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-12 bg-white/5 text-[11px] uppercase tracking-[0.18em] text-white/45 px-6 py-4">
              <div className="col-span-7">Fund Scheme</div>
              <div className="col-span-5 text-right">Trail commission (p.a.)</div>
            </div>
            {SCHEME_GROUPS.map((g, gi) => (
              <div key={g.group}>
                <div
                  className={`px-6 py-3 text-[10px] uppercase tracking-[0.22em] text-[#C8A96B]/85 bg-[#0c1827] ${gi > 0 ? "border-t border-white/5" : ""}`}
                >
                  {g.group}
                </div>
                {g.items.map((s) => {
                  const idx = rowIdx++;
                  return (
                    <div
                      key={s.name}
                      data-testid={`rate-row-${idx}`}
                      className="grid grid-cols-12 items-center px-6 py-4 text-sm border-t border-white/5 hover:bg-white/[0.03] transition"
                    >
                      <div className="col-span-7 text-white/85 font-light">
                        {s.name}
                      </div>
                      <div className="col-span-5 text-right font-mono text-[13px] text-[#C8A96B]/95">
                        {s.trail}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile stacked */}
        <div className="mt-10 md:hidden space-y-5">
          {SCHEME_GROUPS.map((g) => (
            <div key={g.group}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C8A96B]/85 mb-2">
                {g.group}
              </div>
              <div className="space-y-2">
                {g.items.map((s) => (
                  <div
                    key={s.name}
                    data-testid={`rate-row-mobile-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                    className="rounded-xl border border-white/10 bg-[#0c1827]/50 px-4 py-3 flex items-center justify-between"
                  >
                    <span className="text-sm text-white/85 font-light">{s.name}</span>
                    <span className="font-mono text-[12px] text-[#C8A96B]/95">{s.trail}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-start gap-3 text-xs text-white/40 leading-relaxed max-w-3xl">
          <AlertTriangle size={13} className="text-[#C8A96B]/70 mt-0.5 shrink-0" />
          Rates are published on a best-effort basis and are updated as and
          when actual rates are received from AMCs. The numbers above are
          indicative ranges for the {DISCLOSURE_PERIOD} disclosure window —
          actual amounts vary by scheme, plan, holding period, and any
          applicable claw-back.
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── SCHEME-WISE PDF DOWNLOAD ─────────────────── */
function SchemeDownload() {
  return (
    <section
      data-testid="commissions-download"
      className="py-14 md:py-16 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-center rounded-2xl border border-white/10 bg-white/[0.02] p-8 md:p-10">
          <div className="md:col-span-2 flex md:justify-center">
            <div className="w-14 h-14 rounded-xl bg-[#C8A96B]/10 border border-[#C8A96B]/30 flex items-center justify-center">
              <FileText size={24} strokeWidth={1.3} className="text-[#C8A96B]" />
            </div>
          </div>
          <div className="md:col-span-7">
            <SectionEyebrow>Scheme-wise breakdown</SectionEyebrow>
            <h3 className="mt-3 text-xl md:text-2xl font-light text-white tracking-tight">
              Full scheme-wise commission disclosure (PDF).
            </h3>
            <p className="mt-3 text-sm md:text-base text-white/55 font-light leading-relaxed">
              Every scheme we distribute, with the exact trail commission
              applicable for the {DISCLOSURE_PERIOD} window.
            </p>
          </div>
          <div className="md:col-span-3 md:text-right">
            <a
              href="/docs/scheme-wise-commissions.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="commissions-download-link"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#C8A96B]/50 text-[#C8A96B] hover:bg-[#C8A96B]/10 transition text-sm"
            >
              Download PDF
              <Download size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── DISTRIBUTOR NOTE ─────────────────── */
function DistributorNote() {
  return (
    <section
      data-testid="commissions-note"
      className="py-14 md:py-16 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-4">
          <SectionEyebrow>Note</SectionEyebrow>
          <h2 className="mt-4 text-2xl md:text-3xl font-light tracking-[-0.02em] leading-[1.2]">
            We are a{" "}
            <span className="italic text-[#C8A96B]">distributor</span> — not a
            broker.
          </h2>
        </div>
        <div className="md:col-span-8 space-y-5">
          <CornerCard>
            <p className="text-sm md:text-base text-white/70 leading-relaxed">
              Kinntegra Wealth Private Limited (AMFI Registered Mutual Funds
              Distributor) acts as a{" "}
              <span className="text-white">distributor</span> of mutual funds
              wherein we distribute products of various mutual funds. We have{" "}
              <span className="text-white">no business conflict of interest</span>.
            </p>
          </CornerCard>
          <CornerCard>
            <p className="text-sm md:text-base text-white/70 leading-relaxed">
              Clients may also consider other alternate products not offered
              by us before making the investment decision.
            </p>
          </CornerCard>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── BACK CTA ─────────────────── */
function BackToInvestors() {
  return (
    <section
      data-testid="commissions-cta"
      className="py-16 md:py-20"
    >
      <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <p className="text-sm uppercase tracking-[0.22em] text-white/40 mb-4">
          For investors
        </p>
        <h2 className="text-2xl md:text-3xl font-light tracking-[-0.02em] leading-[1.2]">
          Have a question about how this applies to your portfolio?
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <Link
            to="/private-investors"
            data-testid="commissions-back-investors"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition"
          >
            Back to Private Investors
            <ArrowUpRight size={16} />
          </Link>
          <a
            href="https://www.amfiindia.com/investor#online-centre"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white hover:bg-white/5 transition"
          >
            Visit AMFI Investor Corner
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── shared ─────────────────── */
function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
      <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
      {children}
    </div>
  );
}

function CornerCard({ children }) {
  return (
    <div className="relative rounded-2xl border border-white/10 bg-[#0c1827]/60 p-6">
      <span className="absolute top-4 left-4 h-3 w-3 border-t border-l border-[#C8A96B]/40" />
      <span className="absolute top-4 right-4 h-3 w-3 border-t border-r border-[#C8A96B]/40" />
      <span className="absolute bottom-4 left-4 h-3 w-3 border-b border-l border-[#C8A96B]/40" />
      <span className="absolute bottom-4 right-4 h-3 w-3 border-b border-r border-[#C8A96B]/40" />
      {children}
    </div>
  );
}
